import { useEffect, useState } from 'react'

/**
 * Public, pre-auth, read-only venue lookup page (`#/v/:slug`).
 *
 * Loser-facing surface: someone who thinks they lost an item at a venue lands
 * here (link, QR, or pasted URL) and can scan recently-found items. No staff
 * controls, no claim flow — just a list and a CTA back into the consumer
 * lost-report flow with the venue slug pre-filled in the query string.
 *
 * The API endpoint (`GET /api/venues/:slug/found-items`) returns columns
 * filtered through ITEM_PUBLIC_COLUMNS, so PII (reporter contact, voice
 * transcripts, notes, serial numbers) never reaches this component.
 */

interface PublicItem {
  id: string
  category?: string | null
  subcategory?: string | null
  brand?: string | null
  model?: string | null
  colorPrimary?: string | null
  colorSecondary?: string | null
  material?: string | null
  size?: string | null
  distinguishingMarks?: string | null
  venueName?: string | null
  specificLocation?: string | null
  dateOccurred?: string | null
  timeApproximate?: string | null
  referenceCode?: string | null
  photoUrls?: string[] | null
  createdAt?: string | null
}

interface FoundItemsResponse {
  venue: { id: string; name: string; slug: string }
  items: PublicItem[]
  count: number
}

interface VenuePublicProps {
  slug: string
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  try {
    // dateOccurred is a date string (YYYY-MM-DD); normalize to noon UTC so
    // toLocaleDateString doesn't drift across timezones.
    const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return null
  }
}

function describeItem(item: PublicItem): string {
  // Single-line item heading: "Brand Subcategory" → "Subcategory" → "Category".
  const parts = [item.brand, item.subcategory ?? item.category].filter(Boolean)
  if (parts.length === 0) return 'Item'
  return parts.join(' ')
}

function describeColor(item: PublicItem): string | null {
  const colors = [item.colorPrimary, item.colorSecondary].filter(Boolean)
  return colors.length > 0 ? colors.join(' / ') : null
}

export default function VenuePublic({ slug }: VenuePublicProps) {
  const [data, setData] = useState<FoundItemsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setData(null)
    setNotFound(false)
    setError(null)

    fetch(`/api/venues/${encodeURIComponent(slug)}/found-items`)
      .then(async (res) => {
        if (cancelled) return
        if (res.status === 404) {
          setNotFound(true)
          return
        }
        if (!res.ok) {
          setError(`Server returned ${res.status}`)
          return
        }
        const body = (await res.json()) as FoundItemsResponse
        setData(body)
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Network error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  // ── 404 ──
  if (!loading && notFound) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <header className="shrink-0 pt-6 pb-2 text-center">
          <a href="#/" className="inline-block">
            <p className="text-[10px] font-medium tracking-[0.18em] uppercase transition-colors hover:opacity-70" style={{ color: 'var(--text-ghost)' }}>
              The Lost Club
            </p>
          </a>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Venue not found
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
            We couldn't find a venue called <span className="font-mono">{slug}</span>.
          </p>
          <a
            href="#/"
            className="text-sm hover:underline"
            style={{ color: 'var(--accent)' }}
          >
            Report a lost item anyway
          </a>
        </div>
      </div>
    )
  }

  // ── Generic error ──
  if (!loading && error && !data) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <header className="shrink-0 pt-6 pb-2 text-center">
          <p className="text-[10px] font-medium tracking-[0.18em] uppercase" style={{ color: 'var(--text-ghost)' }}>
            The Lost Club
          </p>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-sm mb-4" style={{ color: 'var(--recording)' }}>
            Couldn't load the venue. Please try again in a moment.
          </p>
          <a href="#/" className="text-sm hover:underline" style={{ color: 'var(--accent)' }}>
            Go to home
          </a>
        </div>
      </div>
    )
  }

  // ── Loading ──
  if (loading || !data) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <header className="shrink-0 pt-6 pb-2 text-center">
          <p className="text-[10px] font-medium tracking-[0.18em] uppercase" style={{ color: 'var(--text-ghost)' }}>
            The Lost Club
          </p>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            className="text-[var(--text-ghost)]"
            style={{ animation: 'spin 1s linear infinite' }}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
              strokeDasharray="60 30"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    )
  }

  // ── Loaded ──
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Header */}
      <header className="shrink-0 pt-6 pb-4 text-center">
        <a href="#/" className="inline-block">
          <p className="text-[10px] font-medium tracking-[0.18em] uppercase transition-colors hover:opacity-70" style={{ color: 'var(--text-ghost)' }}>
            The Lost Club
          </p>
        </a>
        <h2 className="mt-3 text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
          {data.venue.name}
        </h2>
        <p className="mt-1 text-[11px] tracking-widest uppercase" style={{ color: 'var(--text-ghost)' }}>
          Items found here
        </p>
      </header>

      {/* Scrollable list */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
        <div className="max-w-lg mx-auto space-y-3">
          {data.items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                No items currently found at this venue.
              </p>
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Lost something? Tap below — we'll let staff know to keep an eye out.
              </p>
            </div>
          ) : (
            data.items.map((item) => {
              const heading = describeItem(item)
              const color = describeColor(item)
              const date = formatDate(item.dateOccurred ?? item.createdAt)
              const photo = item.photoUrls && item.photoUrls.length > 0 ? item.photoUrls[0] : null
              return (
                <div
                  key={item.id}
                  className="rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-subtle)] overflow-hidden flex"
                  style={{ boxShadow: '0 2px 8px rgba(26, 22, 20, 0.06)' }}
                >
                  {photo && (
                    <div className="shrink-0 w-24 h-24 bg-[var(--bg-surface-2)]">
                      <img
                        src={photo}
                        alt={heading}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 px-4 py-3">
                    <p className="text-sm font-medium capitalize" style={{ color: 'var(--text-primary)' }}>
                      {heading}
                    </p>
                    {color && (
                      <p className="mt-0.5 text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>
                        {color}
                      </p>
                    )}
                    {item.distinguishingMarks && (
                      <p className="mt-1 text-xs line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                        {item.distinguishingMarks}
                      </p>
                    )}
                    {date && (
                      <p className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        Found {date}
                      </p>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Bottom CTA — links into the consumer lost-report flow with venue slug */}
      <div
        className="shrink-0 px-4 pt-3 pb-4 border-t border-[var(--border-subtle)]"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}
      >
        <a
          href={`#/?venueSlug=${encodeURIComponent(data.venue.slug)}`}
          className="
            block w-full text-center py-3 rounded-xl text-sm font-medium
            transition-colors hover:brightness-110
          "
          style={{ background: 'var(--accent)', color: 'var(--text-on-accent)' }}
        >
          Lost something here?
        </a>
        {/* Google Places attribution — required by Places API terms when
            displaying place data (venue names resolved via Places search). */}
        <p
          className="mt-3 text-center text-[10px] tracking-wide"
          style={{ color: 'var(--ink-4)' }}
        >
          Venue data powered by Google
        </p>
      </div>
    </div>
  )
}
